// covered-by: frontends/carrie/src/components/ask-puntt-review/__tests__/resolveEmbeddedCitationView.test.ts
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";
import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";

import {
  buildCitableContextFiles,
  findContextFileIndex,
} from "../../routes/ask-puntt/chatUtils";
import {
  type AssetFile,
  type HighlightPosition,
} from "../../routes/ask-puntt/components/AssetViewerPane";
import {
  citationToTarget,
  getNavigableCitations,
} from "../../routes/ask-puntt/utils/commentSourceUtils";

export type EmbeddedCitationView = {
  sourceAssets: AssetFile[];
  initialAssetIndex: number;
  initialViewerPage: number;
  citationFocusPin: { relX: number; relY: number; page: number } | null;
  highlightPosition: HighlightPosition | null;
};

export function resolveEmbeddedCitationView(
  contextFiles: readonly AssetFile[],
  reviewFiles: readonly AssetFile[],
  comment: AskPunttReviewComment,
): EmbeddedCitationView | null {
  const sourceAssets = buildCitableContextFiles(
    [...contextFiles],
    [...reviewFiles],
    [comment],
  ) as AssetFile[];

  if (sourceAssets.length === 0) return null;

  const navigable = getNavigableCitations(comment as BaseTicketComment);
  const citationTarget = navigable[0] ? citationToTarget(navigable[0]) : null;

  const matchedIndex = citationTarget
    ? findContextFileIndex(
        sourceAssets,
        citationTarget.sourceUrl,
        citationTarget.originalURL,
      )
    : -1;
  const initialAssetIndex = matchedIndex >= 0 ? matchedIndex : 0;
  const initialViewerPage = citationTarget?.pageNumber ?? 1;

  let citationFocusPin: EmbeddedCitationView["citationFocusPin"] = null;
  let highlightPosition: HighlightPosition | null = null;

  if (
    citationTarget?.position?.x != null &&
    citationTarget.position.y != null &&
    citationTarget.pageNumber != null
  ) {
    citationFocusPin = {
      relX: citationTarget.position.x,
      relY: citationTarget.position.y,
      page: citationTarget.pageNumber,
    };
  } else if (citationTarget) {
    highlightPosition = {
      x: citationTarget.position?.x,
      y: citationTarget.position?.y,
      page: citationTarget.pageNumber,
      cellAnchor: citationTarget.cellAnchor,
      videoStart: citationTarget.videoStart,
    };
  }

  return {
    sourceAssets,
    initialAssetIndex,
    initialViewerPage,
    citationFocusPin,
    highlightPosition,
  };
}
